import React, { useCallback, useEffect, useState, useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import BusyIndicator from '../busyIndicator';
import AuthenticationContext from './AuthenticationContext';
import {
  ILoginResult,
  IUserData,
  IVisitorTypeDetails,
  INormalizedObjects,
  INormalizedVisitorTypeDetails,
} from './types';
import { localStorageService } from '../../services';
import jwtDecode from 'jwt-decode';
import {
  userReducer,
  initialState,
  loginSucceeded,
  logoutSucceeded,
  licenseUpdated,
} from './reducer';
import validateToken from './validateToken';

const AuthenticationProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const [busy, setBusy] = useState<boolean>(true);
  const [state, dispatch] = useReducer(userReducer, initialState);

  const handleLogin = useCallback(
    (loginResult: ILoginResult, path?: string) => {
      const {
        hasValidLicense,
        licenseHasExpired,
        numberOfSites,
        showNagScreen,
        daysUntilLicenseExpires,
        retainDataForInDays,
        dataPrivacyMessage,
        visitorTypes,
        questionnaires,
        agreements,
        token,
      } = loginResult;

      localStorageService.setToken(token);

      const decodedJwtToken: any = jwtDecode(token);

      const themeId: string | undefined = decodedJwtToken['tid'];

      const userData: IUserData = {
        pin: decodedJwtToken[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
        ],
        site: decodedJwtToken[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn'
        ],
        role: decodedJwtToken[
          'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
        ],
        hasValidLicense: hasValidLicense ?? true,
        licenseHasExpired: licenseHasExpired ?? false,
        numberOfSites: numberOfSites ?? 0,
        ...(showNagScreen && { showNagScreen }),
        ...(daysUntilLicenseExpires && { daysUntilLicenseExpires }),
        ...(retainDataForInDays && { retainDataForInDays }),
        ...(dataPrivacyMessage && { dataPrivacyMessage }),
        visitorTypes: normalizeVisitorTypes(visitorTypes),
        questionnaires: normalize(questionnaires),
        agreements: normalize(agreements),
        ...(themeId && { themeId: Number.parseInt(themeId) }),
      };

      dispatch(loginSucceeded(userData));

      if (path) {
        history.replace(path);
      }
    },
    [history],
  );

  const normalizeVisitorTypes = (
    visitorTypes?: Array<IVisitorTypeDetails>,
  ): INormalizedObjects<INormalizedVisitorTypeDetails> | undefined => {
    if (!visitorTypes) return visitorTypes;

    const parseQuestionnaireIds = (
      visitorType: IVisitorTypeDetails,
    ): Array<number> => {
      if (!visitorType.questionnaireId) return [];
      if (!visitorType.supplementaryQuestionnaireId)
        return [visitorType.questionnaireId];
      return [
        visitorType.questionnaireId,
        visitorType.supplementaryQuestionnaireId,
      ];
    };

    const byId: { [id: number]: INormalizedVisitorTypeDetails } = {};
    const allIds: Array<number> = [];

    for (let index = 0; index < visitorTypes.length; index++) {
      byId[visitorTypes[index].id] = {
        id: visitorTypes[index].id,
        name: visitorTypes[index].name,
        icon: visitorTypes[index].icon,
        agreementId: visitorTypes[index].agreementId,
        questionnaires: parseQuestionnaireIds(visitorTypes[index]),
      };

      allIds.push(visitorTypes[index].id);
    }

    return {
      byId,
      allIds,
    };
  };

  const normalize = <T extends { id: number }>(
    items?: Array<T>,
  ): INormalizedObjects<T> | undefined => {
    if (!items) return items;

    const byId: { [id: number]: T } = {};
    const allIds: Array<number> = [];

    for (let index = 0; index < items.length; index++) {
      byId[items[index].id] = items[index];
      allIds.push(items[index].id);
    }

    return {
      byId,
      allIds,
    };
  };

  const handleLogout = useCallback(() => {
    localStorageService.clearToken();
    dispatch(logoutSucceeded());
    history.push('/');
  }, [history]);

  // when the component mounts check to see if a token already exists
  // this could happen on either a page refresh or if the user didn't log out last time
  useEffect(() => {
    const token = localStorageService.getToken();

    if (token) {
      validateToken(token)
        .then(({ data }: { data: ILoginResult }) => handleLogin(data))
        .catch((error) => {
          console.error(error);
          handleLogout();
        })
        .finally(() => setBusy(false));
    } else {
      setBusy(false);
    }
  }, [handleLogin, handleLogout]);

  if (busy) return <BusyIndicator text="Authenticating" fullScreen />;

  return (
    <AuthenticationContext.Provider
      value={{
        ...state,
        hasRole: (role) => state.role === role,
        onLogin: handleLogin,
        onLogout: handleLogout,
        onLicenseUpdated: (licenseUpdatedResult) =>
          dispatch(licenseUpdated(licenseUpdatedResult)),
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
